package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.tr
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import kotlin.random.Random
import dev.lingvo.site.i18n.t

/**
 * A 40 px numeric field with a localized Mixed state, bounded commits and optional label scrubbing.
 * Text is a draft until Enter or blur; arrows/buttons commit one step. Escape discards a draft or
 * cancels a scrub. Mixed values cannot scrub because a Double preview cannot restore null. [onScrub] receives previews and [onValueChange] receives the final scrub value.
 * [unit] is a literal symbol, and [decimals] supports zero through ten fractional digits.
 */
@Composable
fun LNumberField(
    value: Double?,
    onValueChange: (Double) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    unit: String? = null,
    step: Double = 1.0,
    bigStep: Double = 10.0,
    min: Double? = null,
    max: Double? = null,
    decimals: Int = 0,
    onScrub: ((Double) -> Unit)? = null,
    enabled: Boolean = true,
) {
    val model = remember(step, bigStep, min, max, decimals) { LNumberFieldModel(step, bigStep, min, max, decimals) }
    val inputId = remember { "lingvo-number-${Random.nextInt(1_000_000)}" }
    var committed by remember { mutableStateOf(value) }
    var draft by remember { mutableStateOf(model.display(value)) }
    var dirty by remember { mutableStateOf(false) }
    var scrubbing by remember { mutableStateOf(false) }
    var scrubOrigin by remember { mutableStateOf(value) }
    var scrubX by remember { mutableStateOf(0.0) }
    var scrubPointer by remember { mutableStateOf<Int?>(null) }
    var scrubValue by remember { mutableStateOf(value) }
    var scrubMoved by remember { mutableStateOf(false) }
    var previousValue by remember { mutableStateOf(value) }
    var previousModel by remember { mutableStateOf(model) }
    var previousEnabled by remember { mutableStateOf(enabled) }
    val currentChange by rememberUpdatedState(onValueChange)
    val currentScrub by rememberUpdatedState(onScrub)
    val currentEnabled by rememberUpdatedState(enabled)
    val currentModel by rememberUpdatedState(model)
    fun restore() {
        draft = currentModel.display(committed)
        dirty = false
    }
    fun accept(number: Double) {
        committed = number
        draft = currentModel.display(number)
        dirty = false
        currentChange(number)
    }
    fun commit() {
        if (!currentEnabled || !dirty) return
        model.commit(draft)?.let(::accept) ?: restore()
    }
    fun stepValue(direction: Int, shift: Boolean) {
        if (currentEnabled) accept(model.stepped(model.commit(draft) ?: committed, direction, shift))
    }
    // Reconcile each parent input during composition. A launched effect can run after the next
    // pointer move and mistake its captured, older value for a new authoritative replacement.
    val valueChanged = value != previousValue
    if (valueChanged || model != previousModel || enabled != previousEnabled) {
        previousValue = value
        previousModel = model
        previousEnabled = enabled
        if (scrubbing && valueChanged && value != scrubValue) {
            // A parent may echo our own preview, but a different value is authoritative. Retire
            // this gesture without replaying its old origin over the external update.
            scrubbing = false
            scrubPointer = null
            scrubMoved = false
            committed = value
            restore()
        } else if (!scrubbing) {
            committed = value
            if (!dirty || !enabled) restore()
        }
    }
    DisposableEffect(enabled, onScrub != null) {
        val move: (Event) -> Unit = { event ->
            if (scrubbing && currentEnabled && (event.asDynamic().pointerId as Int) == scrubPointer) {
                val next = currentModel.scrubbed(scrubOrigin, (event.asDynamic().clientX as Double) - scrubX,
                    event.asDynamic().shiftKey as Boolean)
                scrubMoved = scrubMoved || (event.asDynamic().clientX as Double) != scrubX
                scrubValue = next
                draft = currentModel.display(next)
                currentScrub?.invoke(next)
                event.preventDefault()
            }
        }
        val end: (Event) -> Unit = { event ->
            if (scrubbing && (event.asDynamic().pointerId as Int) == scrubPointer) {
                scrubbing = false
                scrubPointer = null
                if (currentEnabled && scrubMoved) scrubValue?.let(::accept) else restore()
            }
        }
        val cancel: (Event) -> Unit = {
            if (scrubbing && (it.asDynamic().pointerId == null || (it.asDynamic().pointerId as Int) == scrubPointer)) {
                scrubbing = false
                scrubPointer = null
                scrubOrigin?.let { original -> currentScrub?.invoke(original) }
                committed = scrubOrigin
                restore()
            }
        }
        val key: (Event) -> Unit = { event ->
            if ((event as KeyboardEvent).key == "Escape" && scrubbing) {
                event.preventDefault()
                cancel(event)
            }
        }
        document.addEventListener("pointermove", move)
        document.addEventListener("pointerup", end)
        document.addEventListener("pointercancel", cancel)
        document.addEventListener("keydown", key)
        onDispose {
            document.removeEventListener("pointermove", move)
            document.removeEventListener("pointerup", end)
            document.removeEventListener("pointercancel", cancel)
            document.removeEventListener("keydown", key)
            if (scrubbing) {
                scrubOrigin?.let { currentScrub?.invoke(it) }
                scrubbing = false
                scrubPointer = null
                committed = scrubOrigin
                restore()
            }
        }
    }
    Column(LFieldGroupStyle.toModifier().then(modifier).attr("data-disabled", (!enabled).toString())) {
        Label(forId = inputId, attrs = LFieldLabelStyle.toModifier().styleModifier {
            if (onScrub != null && enabled && committed != null) {
                property("cursor", "ew-resize")
                property("touch-action", "none")
                property("user-select", "none")
            }
        }.toAttrs {
            ref { element ->
                val start: (Event) -> Unit = { event ->
                    if (currentEnabled && currentScrub != null && committed != null &&
                        (event.asDynamic().button as Int) == 0) {
                        event.preventDefault()
                        scrubbing = true
                        scrubPointer = event.asDynamic().pointerId as Int
                        scrubOrigin = committed
                        scrubValue = committed
                        scrubMoved = false
                        scrubX = event.asDynamic().clientX as Double
                        (document.getElementById(inputId) as? HTMLInputElement)?.focus()
                    }
                }
                element.addEventListener("pointerdown", start)
                onDispose { element.removeEventListener("pointerdown", start) }
            }
        }) { Text(label) }
        Row(LTextFieldFrameStyle.toModifier(), verticalAlignment = Alignment.CenterVertically) {
            Input(InputType.Text, attrs = LTextFieldInputStyle.toModifier().toAttrs {
                id(inputId)
                value(draft)
                placeholder(tr("ds.ds.lnumber_field.mixed", "Mixed"))
                attr("role", "spinbutton")
                attr("inputmode", "decimal")
                attr("aria-label", label)
                committed?.let { attr("aria-valuenow", it.toString()) }
                if (committed == null) attr("aria-valuetext", tr("ds.ds.lnumber_field.mixed", "Mixed"))
                min?.let { attr("aria-valuemin", it.toString()) }
                max?.let { attr("aria-valuemax", it.toString()) }
                if (!enabled) disabled()
                onInput { if (currentEnabled) { draft = it.value; dirty = true } }
                onBlur { commit() }
                onKeyDown { event ->
                    if (currentEnabled) when (event.key) {
                        "Enter" -> { event.preventDefault(); commit() }
                        "Escape" -> { event.preventDefault(); restore() }
                        "ArrowUp" -> { event.preventDefault(); stepValue(1, event.shiftKey) }
                        "ArrowDown" -> { event.preventDefault(); stepValue(-1, event.shiftKey) }
                    }
                }
            })
            if (unit != null) Span(Modifier.color(ColorMode.current.toSitePalette().textMuted)
                .padding(right = Space.SM).toAttrs { attr("aria-hidden", "true") }) { Text(unit) }
            listOf(-1, 1).forEach { direction ->
                Button(LColorFieldButtonStyle.toModifier().styleModifier {
                    property("height", "100%")
                    // The frame's 1px borders leave only 38px for percentage height. Keep the
                    // optical field at 40px while its centred step targets span the full height.
                    property("min-height", ControlHeight.MD.toString())
                    property("min-width", "40px")
                    property("padding", "0")
                    property("border-radius", "0")
                }.toAttrs {
                    type(ButtonType.Button)
                    attr("aria-label", if (direction < 0) tr("ds.ds.lnumber_field.decrease", "Decrease" + " ") + label
                        else tr("ds.ds.lnumber_field.increase", "Increase" + " ") + label)
                    attr("data-disabled", (!enabled).toString())
                    if (!enabled) disabled()
                    onMouseDown { it.preventDefault() }
                    onClick { stepValue(direction, it.shiftKey) }
                }) { Text(if (direction < 0) "−" else "+") }
            }
        }
    }
}
